import { change } from 'redux-form';
import { get } from 'lodash';

import { AppDispatch } from '../../../store.type';
import { changeRfiSgKYCScreen, submitRfiData } from '@Redux/actions/rfi/rfi-action';
import { disableLoader } from '@Redux/actions/ui-trigger';
import { SG_ODD_FLOW_TYPES, SG_ODD_SCREEN_NAMES } from './ScreenConstants';
import { store } from '../../../store';
import { AddressObject } from './types';
import { FORMNAME_RFI } from '@Utils/constants';

export const SINGPASS_ROOT_LANDING_PAGE = {
  heading: 'Address verification',
  subHeading: `It’s time to renew your KYC verification.`,
  primaryButtonText: 'Retrieve Myinfo with Singpass',
  sub2Heading: 'No documents required.',
  secondaryButtonText: 'Verify manually',
  sub2Description: ` Scan/upload your ID proof (and proof of address if you don’t have NRIC)`,
  requestHeading: 'Request for additional information'
};

export const ADDRESS_PAGE = {
  heading: `Please provide your address, make sure it's the same as the one on your utility bill or bank statement.`,
  buttonText: 'Submit'
};

export const PERSONAL_INFO_PAGE = {
  heading: `Personal information`,
  buttonText: 'Save and continue'
};

export const POA_SELECTED_PAGE = {
  heading: 'Upload address document'
};

export const POA_UPLOAD_PAGE = {
  heading: 'Address verification',
  description: 'Please upload one of the following documents so we can proceed.'
};

export const SUCCESS_PAGE = {
  APPROVED: {
    heading: 'You’re all set!',
    description: 'Start sending money to your friends, family or anyone.',
    primaryButton: 'Send money',
    secondaryButton: 'Go to Home'
  },
  ACKNOWLEDGED: {
    heading: 'Reviewing your details',
    description:
      'Our team is reviewing your account details. We’ll get back to you within 1 business day.',
    primaryButton: 'Send money',
    secondaryButton: 'Go to Home'
  }
};

export const populateFormWithAddressData = (
  addressResponse: Record<string, string>,
  nestedFieldsJson: AddressObject[],
  formName: string,
  dispatch: AppDispatch
): boolean => {
  let allFieldsHaveValue = true;

  nestedFieldsJson?.forEach((field: AddressObject) => {
    const fieldName = field.name;
    const fullFieldName = field.field_name;
    // Get the corresponding value from address response
    const addressValue = addressResponse?.[fieldName];

    if (addressValue) {
      dispatch(change(formName, fullFieldName, addressValue));
    } else {
      allFieldsHaveValue = false; // Found at least one empty field
    }
  });
  return allFieldsHaveValue; // true if all fields have value, false otherwise
};

interface AddressDocument {
  is_address_changed?: boolean;
  flow_type?: string;
  unit_number?: string;
  floor_number?: string;
  building_name?: string;
  street_name?: string;
  street_number?: string;
  city?: string;
  district?: string;
  building_number?: string;
  postcode?: string;
  suburb?: string;
  province?: string;
  state?: string;
  town?: string;
  address1?: string;
  address2?: string;
  block_number?: string;
  street_type?: string;
}

interface NestedFieldsData {
  is_address_changed: {
    details: Record<string, any>;
  };
}

interface RFIResponseItem {
  document_detail_id: null;
  is_KYC_oriented: boolean;
  is_transaction_oriented: boolean;
  identity_number: string | null;
  date_of_issue: null;
  date_of_expiry: null;
  code: string;
  is_back_document: boolean;
  is_address_changed: boolean;
  nested_fields_data: NestedFieldsData;
}

interface RFIResponse {
  [rfiId: string]: RFIResponseItem;
}

const processPayload = (
  getAllFormVal: Record<string, any>,
  additionalFields: AdditionalFields | undefined,
  docFieldName: string,
  isFrontDocumentExists: boolean = false,
  isBackDocumentExists: boolean = false
): RFIResponse => {
  const rfiResponse: RFIResponse = {};

  // Extract globalRFIId from the first key of getAllFormVal and get just the ID number
  const fullRFIKey: string = Object.keys(getAllFormVal)[0];
  const globalRFIId: string = fullRFIKey.split(':')[1]; // Extract just the number part

  // Get the nested object containing ADDRESS_POA_DOCUMENT
  const rfiData = getAllFormVal[fullRFIKey];

  // Initialize the RFI response structure
  rfiResponse[globalRFIId] = {
    document_detail_id: null,
    is_KYC_oriented: false,
    is_transaction_oriented: false,
    identity_number: null,
    date_of_issue: null,
    date_of_expiry: null,
    code: 'ADDRESS_POA_DOCUMENT',
    is_back_document: isBackDocumentExists,
    is_address_changed: false,
    nested_fields_data: {
      is_address_changed: {
        details: {}
      }
    }
  };

  Object.keys(rfiResponse[globalRFIId]).forEach((field) => {
    if (Object.prototype.hasOwnProperty.call(additionalFields || {}, field)) {
      (rfiResponse[globalRFIId] as any)[field] = additionalFields?.[field];
    }
  });

  // Process ADDRESS_POA_DOCUMENT if it exists
  if (rfiData && rfiData.ADDRESS_POA_DOCUMENT) {
    const documentData: AddressDocument = rfiData.ADDRESS_POA_DOCUMENT;

    // Set the code
    rfiResponse[globalRFIId].code = 'ADDRESS_POA_DOCUMENT';

    // Set is_address_changed from the document data
    rfiResponse[globalRFIId].is_address_changed = documentData.is_address_changed || false;

    // Create nested_fields_data structure
    rfiResponse[globalRFIId].nested_fields_data = {
      is_address_changed: {
        ...(isFrontDocumentExists ? { fieldname: docFieldName } : {}),
        details: {}
      }
    };

    // Copy all address fields to details, excluding is_address_changed
    Object.keys(documentData).forEach((fieldKey: string) => {
      if (fieldKey !== 'is_address_changed') {
        rfiResponse[globalRFIId].nested_fields_data.is_address_changed.details[fieldKey] =
          documentData[fieldKey as keyof AddressDocument];
      }
    });

    if (
      rfiResponse[globalRFIId].nested_fields_data.is_address_changed.details['flow_type'] ===
      SG_ODD_FLOW_TYPES.FIN
    ) {
      const reduxStore = store.getState();
      const documentType = get(
        reduxStore,
        'rootReducer.rfiReducer.sgKycFlow.selectedAddressDoc.document_code',
        ''
      );
      rfiResponse[globalRFIId].nested_fields_data.is_address_changed.details['document_type'] =
        documentType;
    }
  }

  return rfiResponse;
};

export const changeFormField = (
  radioFieldName: string,
  fieldName: string,
  value: string | boolean,
  dispatch: AppDispatch
) => {
  dispatch(change(FORMNAME_RFI, radioFieldName.replace(/\.[^.]+$/, `.${fieldName}`), value));
};

interface AdditionalFields {
  getAllformVal?: Record<string, string> | undefined;
  frontFiles?: FileList | null;
  backFiles?: FileList | null;
  [key: string]: any; // Allow additional properties
}

export const submitSgKycRfi = async (
  getAllFormVal: Record<string, any>,
  dispatch: AppDispatch,
  additionalFields: AdditionalFields = {}
) => {
  const fullRFIKey: string = Object.keys(getAllFormVal)?.[0];
  const docFieldName = `${fullRFIKey || ''}_address_poa_document_doc_file`.toLowerCase();
  const isFrontDocumentExists =
    additionalFields?.frontFiles?.[0] || additionalFields?.finalFirstFile;
  const isBackDocumentExists =
    !!(additionalFields?.backFiles && additionalFields.backFiles.length > 0) ||
    !!additionalFields?.finalBackFile;

  const processedData = processPayload(
    { ...getAllFormVal },
    additionalFields?.getAllformVal,
    docFieldName,
    isFrontDocumentExists,
    isBackDocumentExists
  );

  const payload = new FormData();
  payload.append('rfi_response', JSON.stringify(processedData));

  // Add document fields only if files exist
  if (
    (additionalFields?.frontFiles && additionalFields?.frontFiles?.length > 0) ||
    additionalFields?.finalFirstFile
  ) {
    /* 
      frontFiles will have value if there was no capture img option.
      finalFirstFile[0] will have value if there was capture img option still user used upload img option
      finalFirstFile will have value if there was capture img option and user used capture img option

    */
    //@ts-ignore
    payload.append(
      docFieldName,
      additionalFields?.frontFiles?.[0] ||
        additionalFields?.finalFirstFile?.[0] ||
        additionalFields?.finalFirstFile
    );
  }

  if (
    (additionalFields?.backFiles && additionalFields?.backFiles?.length > 0) ||
    additionalFields.finalBackFile
  ) {
    //@ts-ignore
    payload.append(
      'additional_docs',
      additionalFields.backFiles?.[0] ||
        additionalFields?.finalBackFile?.[0] ||
        additionalFields?.finalBackFile
    );
  }

  const response = await dispatch(submitRfiData(payload));
  if (response?.success) {
    if (response?.data?.is_address_verified === false) {
      dispatch(changeRfiSgKYCScreen(SG_ODD_SCREEN_NAMES.poa_upload_page));
      return;
    } else {
      dispatch(changeRfiSgKYCScreen(SG_ODD_SCREEN_NAMES.success_page));
      return;
    }
  } else {
    const reduxStore = store.getState();
    const sgKycCurrentScreen = get(
      reduxStore,
      'rootReducer.rfiReducer.sgKycFlow.currentScreen',
      ''
    );
    if (
      sgKycCurrentScreen === SG_ODD_SCREEN_NAMES.poi_poa_upload_page ||
      sgKycCurrentScreen === SG_ODD_SCREEN_NAMES.poa_upload_page
    ) {
      dispatch(changeRfiSgKYCScreen(sgKycCurrentScreen));
      return;
    } else dispatch(changeRfiSgKYCScreen(SG_ODD_SCREEN_NAMES.address_page));
  }
  dispatch(disableLoader());
};
